import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, InputNumber, Select, Switch, Tag } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import EditableRowCard from "./EditableRowCard";
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import OpcUaTextField from "./OpcUaTextField";
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
// FLATBUFFER ENUMS
import { HistoryReadKind } from "../../../../../flatbuffers/generated/opc-ua/history-read-kind";
// INTERFACES
import type {
  OpcUaHistoryReadForm,
  OpcUaNodeIdForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;
const { Option } = Select;

const HISTORY_READ_KIND_OPTIONS: { value: HistoryReadKind; label: string }[] = [
  { value: HistoryReadKind.Raw, label: "Raw" },
  { value: HistoryReadKind.Modified, label: "Modified" },
  { value: HistoryReadKind.Processed, label: "Processed" },
  { value: HistoryReadKind.AtTime, label: "AtTime" },
  { value: HistoryReadKind.Events, label: "Events" },
];

function describeNode(node: OpcUaNodeIdForm): string {
  if (node.identifierKind === "browsepath") {
    return `browsepath(${(node.hops ?? []).map((h) => h.targetName).join("/")})`;
  }
  return `ns=${node.namespaceIndex};${node.identifierKind}=${node.identifier}`;
}

function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

const blankRead = (): OpcUaHistoryReadForm => ({
  node: { ...BLANK_NUMERIC_NODE_ID },
  kind: HistoryReadKind.Raw,
  startTime: "",
  endTime: "",
  numValuesPerNode: 0,
  returnBounds: false,
  aggregateTypeNode: null,
  processingIntervalMs: 0,
});

/** The fields of one history read. Shared by the create form and a card's in-place edit. */
function HistoryReadForm({
  read,
  onChangeRead,
  disabled = false,
}: {
  read: OpcUaHistoryReadForm;
  onChangeRead: (read: OpcUaHistoryReadForm) => void;
  disabled?: boolean;
}) {
  const [t] = useTranslation("global");
  return (
    // While the create form is parked behind an in-place edit, the nested
    // editors (which have no disabled prop of their own) are frozen too.
    <div
      aria-disabled={disabled || undefined}
      style={disabled ? { pointerEvents: "none", opacity: 0.6 } : undefined}
    >
      <NodeIdEditor
        node={read.node}
        onChangeNode={(node) => onChangeRead({ ...read, node })}
        disabled={disabled}
      />
      <div className="row mt-2">
        <div className="col-12 col-md-4 mt-2">
          <OpcUaFieldLabel label="Read Kind" tooltip={t("general.opcUaHistoryKindHelp")} />
          <Select
            className="generalStyles__width100"
            style={{ width: "100%", marginTop: 8 }}
            size="large"
            value={read.kind}
            onChange={(kind: HistoryReadKind) => onChangeRead({ ...read, kind })}
          >
            {HISTORY_READ_KIND_OPTIONS.map((opt) => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </div>
        <div className="col-12 col-md-4 mt-2">
          <OpcUaTextField
            label="Start Time (ISO 8601)"
            value={read.startTime}
            onChange={(value) => onChangeRead({ ...read, startTime: value })}
            tooltip={t("general.opcUaHistoryStartHelp")}
            placeholder="2026-01-31T08:00:00Z"
          />
        </div>
        <div className="col-12 col-md-4 mt-2">
          <OpcUaTextField
            label="End Time (ISO 8601)"
            value={read.endTime}
            onChange={(value) => onChangeRead({ ...read, endTime: value })}
            tooltip={t("general.opcUaHistoryEndHelp")}
            placeholder="2026-01-31T18:00:00Z"
          />
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-12 col-md-4 mt-2">
          <OpcUaFieldLabel
            label="Num Values Per Node"
            tooltip={t("general.opcUaHistoryNumValuesHelp")}
          />
          <InputNumber
            className="generalStyles__width100"
            style={{ width: "100%", marginTop: 8 }}
            size="large"
            min={0}
            value={read.numValuesPerNode}
            onChange={(v) => onChangeRead({ ...read, numValuesPerNode: v ?? 0 })}
          />
        </div>
        <div className="col-12 col-md-4 mt-2">
          <OpcUaFieldLabel
            label="Processing Interval (ms, Processed kind)"
            tooltip={t("general.opcUaHistoryIntervalHelp")}
          />
          <InputNumber
            className="generalStyles__width100"
            style={{ width: "100%", marginTop: 8 }}
            size="large"
            min={0}
            value={read.processingIntervalMs}
            onChange={(v) => onChangeRead({ ...read, processingIntervalMs: v ?? 0 })}
          />
        </div>
        <div className="col-12 col-md-4 mt-2" style={{ textAlign: "center" }}>
          <OpcUaFieldLabel label="Return Bounds" tooltip={t("general.opcUaHistoryBoundsHelp")} />
          <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
            <Switch
              checked={read.returnBounds}
              onChange={(checked) => onChangeRead({ ...read, returnBounds: checked })}
            />
          </div>
        </div>
      </div>
      <div className="row mt-2">
        <div className="col-12 col-md-4 mt-2">
          <OpcUaFieldLabel label="Configure aggregate function (Processed kind)" />
          <div style={{ marginTop: 8 }}>
            <Switch
              checked={read.aggregateTypeNode !== null}
              onChange={(enabled) =>
                onChangeRead({
                  ...read,
                  aggregateTypeNode: enabled ? { ...BLANK_NUMERIC_NODE_ID } : null,
                })
              }
            />
          </div>
        </div>
        {read.aggregateTypeNode && (
          <div className="col-12 col-md-8">
            <span className="generalStyles__inputLabel">Aggregate Function Node</span>
            <NodeIdEditor
              node={read.aggregateTypeNode}
              onChangeNode={(aggregateTypeNode) => onChangeRead({ ...read, aggregateTypeNode })}
              label="Aggregate"
              disabled={disabled}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Historical read configurations (OPC UA Part 11): which node, over what time range, at what resolution. Rows are created, edited in place, and deleted. */
export default function HistoryReadsEditor({
  historyReads,
  onChangeHistoryReads,
}: {
  historyReads: OpcUaHistoryReadForm[];
  onChangeHistoryReads: (rows: OpcUaHistoryReadForm[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draft, setDraft] = useState<OpcUaHistoryReadForm>(blankRead);
  // null = authoring a new row with the create form; an index = editing that
  // stored row in place (its card grows the form, the create form is disabled).
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const canAdd = isNodeComplete(draft.node);
  const onSubmit = () => {
    if (!canAdd) return;
    if (editingIndex !== null) {
      const updated = [...historyReads];
      updated[editingIndex] = draft;
      onChangeHistoryReads(updated);
      setEditingIndex(null);
    } else {
      onChangeHistoryReads([...historyReads, draft]);
    }
    setDraft(blankRead());
  };
  const onEdit = (index: number) => {
    setDraft(historyReads[index]);
    setEditingIndex(index);
  };
  const onCancelEdit = () => {
    setDraft(blankRead());
    setEditingIndex(null);
  };
  const onRemove = (index: number) => {
    onChangeHistoryReads(historyReads.filter((_, i) => i !== index));
    if (editingIndex === index) onCancelEdit();
  };

  const isEditing = editingIndex !== null;

  return (
    <div className="container mt-3" style={{ width: "100%", maxWidth: "none" }}>
      <Collapse>
        <Panel
          header={t("general.opcUaHistoryReads", { total: historyReads.length })}
          key="history-reads"
        >
          {historyReads.map((stored, index) => {
            const editing = editingIndex === index;
            // While editing, the summary follows the draft so it reacts to every change.
            const read = editing ? draft : stored;
            const kindLabel = HISTORY_READ_KIND_OPTIONS.find((o) => o.value === read.kind)?.label;
            return (
              <EditableRowCard
                key={`${stored.node.namespaceIndex}-${stored.node.identifier}-${index}`}
                borderColor="rgba(255, 255, 255, 0.15)"
                tags={<Tag style={{ textAlign: "center" }}>{kindLabel}</Tag>}
                raw={`${describeNode(read.node)} — ${kindLabel} [${read.startTime || "…"} → ${read.endTime || "…"}]`}
                editing={editing}
                dimmed={isEditing && !editing}
                canSave={canAdd}
                deleteTitle="Delete this history read? This cannot be undone."
                onEdit={() => onEdit(index)}
                onSave={onSubmit}
                onCancel={onCancelEdit}
                onRemove={() => onRemove(index)}
              >
                <HistoryReadForm read={draft} onChangeRead={setDraft} />
              </EditableRowCard>
            );
          })}
          <HistoryReadForm read={draft} onChangeRead={setDraft} disabled={isEditing} />
          <div className="row mt-3">
            <div className="col-12">
              <Button
                onClick={onSubmit}
                disabled={!canAdd || isEditing}
                className={`generalStyles__width100 ${canAdd && !isEditing ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
}
